import axios from 'axios';

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await axios.get(GEOLOCATION_API, {
      headers: {
        'accept': 'text/plain'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching geolocation:', error);
    return null;
  }
};

/**
 * Submit lead to CRM
 * @param {Object} leadData - Lead information
 * @param {string} leadData.name - Customer name
 * @param {string} leadData.email - Customer email
 * @param {number} leadData.countryDialCode - Country dial code (e.g., 1 for USA)
 * @param {string} leadData.country - Country code (e.g., "US")
 * @param {string} leadData.phoneNumber - Phone number (numeric only, no prefix)
 * @param {string} leadData.ipAddress - IP address from geolocation
 * @param {string} leadData.description - Lead description/message
 * @param {string} leadData.pageUrl - Current page URL
 * @param {string} leadData.city - City (optional)
 * @param {string} leadData.state - State (optional)
 * @param {string} leadData.geoLocation - Geolocation JSON string (optional)
 * @param {string} leadData.packageTitle - Package title (optional)
 * @param {number} leadData.packageAmount - Package amount (optional)
 */
export const submitLead = async (leadData) => {
  try {
    // Ensure phone number is numeric only (strip any non-numeric characters)
    const cleanedData = {
      ...leadData,
      phoneNumber: leadData.phoneNumber.replace(/\D/g, ''),
      countryDialCode: parseInt(leadData.countryDialCode)
    };

    const response = await axios.post(LEAD_API, cleanedData, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    // Status 200 - Success
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
        statusCode: 200,
        customerId: response.data?.data?.customerId
      };
    }
    
    return {
      success: false,
      statusCode: response.status,
      message: 'An error occurred',
      data: response.data
    };
  } catch (error) {
    console.error('Lead submission error:', error);
    
    if (error.response) {
      const status = error.response.status;
      const responseData = error.response.data;
      
      console.error('API Response:', status, responseData);
      
      // Status 401 - Unauthorized
      if (status === 401) {
        return {
          success: false,
          statusCode: 401,
          message: responseData?.message || 'Unauthorized access. Please contact support.',
          data: responseData
        };
      }
      
      // Status 400 - Bad Request
      if (status === 400) {
        return {
          success: false,
          statusCode: 400,
          message: responseData?.errorMessage || responseData?.data?.message || 'Invalid input. Please check your details.',
          data: responseData
        };
      }
      
      return {
        success: false,
        statusCode: status,
        message: responseData?.message || responseData?.errorMessage || 'An error occurred. Please try again.',
        data: responseData
      };
    }
    
    console.error('Network error:', error.message);
    
    return {
      success: false,
      statusCode: 500,
      message: 'Network error. Please try again later.',
      data: null
    };
  }
};

/**
 * Get country dial code mapping
 */
export const getCountryDialCodes = () => {
  return [
    { code: '+1', country: 'USA/Canada', countryCode: 'US', dialCode: '1' },
    { code: '+44', country: 'UK', countryCode: 'GB', dialCode: '44' },
    { code: '+91', country: 'India', countryCode: 'IN', dialCode: '91' },
    { code: '+61', country: 'Australia', countryCode: 'AU', dialCode: '61' },
    { code: '+86', country: 'China', countryCode: 'CN', dialCode: '86' },
    { code: '+81', country: 'Japan', countryCode: 'JP', dialCode: '81' },
    { code: '+49', country: 'Germany', countryCode: 'DE', dialCode: '49' },
    { code: '+33', country: 'France', countryCode: 'FR', dialCode: '33' },
    { code: '+92', country: 'Pakistan', countryCode: 'PK', dialCode: '92' },
    { code: '+31', country: 'Netherlands', countryCode: 'NL', dialCode: '31' }
  ];
};

/**
 * Prepare lead data for submission
 * @param {Object} formData - Form data
 * @param {Object} geoData - Geolocation data
 * @param {Object} selectedCountry - Selected country object
 * @param {string} packageTitle - Package title (optional)
 * @param {number} packageAmount - Package amount (optional)
 */
export const preparLeadData = (formData, geoData, selectedCountry, packageTitle = '', packageAmount = 0) => {
  return {
    name: formData.name,
    email: formData.email,
    countryDialCode: parseInt(selectedCountry.dialCode),
    country: selectedCountry.countryCode,
    phoneNumber: formData.phone.replace(/\D/g, ''),
    ipAddress: geoData?.data?.ipAddress || '',
    description: formData.message || formData.subject || 'Trademark service inquiry',
    pageUrl: window.location.href,
    city: geoData?.data?.cityName || '',
    state: geoData?.data?.regionName || '',
    geoLocation: geoData ? JSON.stringify(geoData) : '',
    packageTitle: packageTitle,
    packageAmount: packageAmount
  };
};
